import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { ChevronRight, ChevronDown, ExternalLink, Check, X, AlertTriangle, Phone, CheckCheck } from 'lucide-react';
import { drawdownAPI, investorAccountAPI } from '../../services/api';

/**
 * Drawdown Calls section displayed at the top of the Transactions tab on
 * the Investor Account details page. Mirrors the DrawdownDetail layout so
 * the user gets a familiar "expand to see receipts" pattern. Approve / Reject
 * buttons appear inline on pending tranches for users with permission.
 */
const DrawdownCallsSection = ({ accountId, fundFilter, fmt, onChange, currentUser }) => {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [expanded, setExpanded] = useState({});
  const [actionLoading, setActionLoading] = useState(null);
  const [rejectModal, setRejectModal] = useState(null);

  const isAdmin = currentUser?.role === 'admin';
  const isManager = currentUser?.role === 'manager';

  const load = React.useCallback(async () => {
    setLoading(true);
    try {
      const res = await investorAccountAPI.getDrawdownCalls(accountId, fundFilter);
      setRows(res.data || []);
      // auto-expand any call that has pending receipts
      const exp = {};
      (res.data || []).forEach(r => {
        if ((r.receipts || []).some(x => x.approval_status === 'pending_approval')) exp[r.drawdown_investor_id] = true;
      });
      setExpanded(exp);
    } catch { setRows([]); }
    finally { setLoading(false); }
  }, [accountId, fundFilter]);

  React.useEffect(() => { if (accountId) load(); }, [accountId, fundFilter, load]);

  const statusColors = {
    draft: 'bg-gray-100 text-gray-600',
    pending_approval: 'bg-amber-100 text-amber-700',
    approved: 'bg-green-100 text-green-700',
    rejected: 'bg-red-100 text-red-700',
    active: 'bg-blue-100 text-blue-700',
    completed: 'bg-green-100 text-green-700',
  };
  const trancheColors = {
    pending_approval: 'bg-amber-100 text-amber-700',
    approved: 'bg-green-100 text-green-700',
    rejected: 'bg-red-100 text-red-700',
  };
  const label = (s) => (s || '').replace(/_/g, ' ');

  const handleApprove = async (row, receiptId) => {
    if (!window.confirm('Approve this receipt? A "Drawdown Received" transaction will be created in the ledger below.')) return;
    setActionLoading(receiptId);
    try {
      await drawdownAPI.approveReceipt(row.drawdown_id, row.drawdown_investor_id, receiptId);
      await load();
      if (onChange) onChange();
    } catch (err) { alert(err.response?.data?.detail || 'Failed to approve'); }
    finally { setActionLoading(null); }
  };

  const handleReject = async () => {
    if (!rejectModal) return;
    setActionLoading(rejectModal.receiptId);
    try {
      await drawdownAPI.rejectReceipt(rejectModal.row.drawdown_id, rejectModal.row.drawdown_investor_id, rejectModal.receiptId, rejectModal.reason);
      setRejectModal(null);
      await load();
      if (onChange) onChange();
    } catch (err) { alert(err.response?.data?.detail || 'Failed to reject'); }
    finally { setActionLoading(null); }
  };

  const handleBulkApprove = async (drawdownId = null) => {
    const scopeLabel = drawdownId ? 'this drawdown' : 'all drawdowns on this account';
    if (!window.confirm(`Approve every pending receipt across ${scopeLabel}? Receipts you submitted yourself will be skipped if you're a Fund Manager (admins can self-approve).`)) return;
    setActionLoading('bulk');
    try {
      const res = await investorAccountAPI.bulkApproveReceipts(accountId, drawdownId);
      const { approved, skipped_self_made, errors } = res.data || {};
      const parts = [`Approved ${approved || 0} receipt(s)`];
      if (skipped_self_made) parts.push(`${skipped_self_made} skipped (cannot self-approve)`);
      if (errors && errors.length) parts.push(`${errors.length} error(s)`);
      alert(parts.join(' · '));
      await load();
      if (onChange) onChange();
    } catch (err) { alert(err.response?.data?.detail || 'Bulk approve failed'); }
    finally { setActionLoading(null); }
  };

  if (loading) {
    return <div className="bg-white border rounded-lg p-4 mb-4 text-sm text-gray-500" data-testid="drawdown-calls-loading">Loading drawdown calls...</div>;
  }
  if (rows.length === 0) {
    return null; // hide section entirely when there are no calls
  }

  const totalPending = rows.flatMap(r => (r.receipts || []).filter(x => x.approval_status === 'pending_approval')).length;
  const totalPendingAmt = rows.flatMap(r => (r.receipts || []).filter(x => x.approval_status === 'pending_approval')).reduce((s, x) => s + (x.amount || 0), 0);
  const totalCalled = rows.reduce((s, r) => s + (r.called_amount || 0), 0);
  const totalReceived = rows.reduce((s, r) => s + (r.received_amount || 0), 0);
  const totalOutstanding = rows.reduce((s, r) => s + (r.outstanding_amount || 0), 0);

  return (
    <div className="mb-6" data-testid="drawdown-calls-section">
      <div className="flex items-center justify-between mb-2">
        <div className="flex items-center gap-2">
          <Phone size={16} className="text-indigo-600" />
          <h3 className="text-sm font-semibold text-gray-800 uppercase tracking-wider">Drawdown Calls</h3>
          <span className="text-xs text-gray-400">({rows.length})</span>
        </div>
        <div className="flex items-center gap-4 text-xs text-gray-600">
          <span>Called: <strong className="text-indigo-700">{fmt(totalCalled)}</strong></span>
          <span>Received: <strong className="text-green-700">{fmt(totalReceived)}</strong></span>
          <span>Outstanding: <strong className="text-red-600">{fmt(totalOutstanding)}</strong></span>
        </div>
      </div>

      {totalPending > 0 && (
        <div className="mb-3 p-3 bg-amber-50 border border-amber-200 rounded-lg flex items-center justify-between gap-2 text-sm text-amber-800" data-testid="dc-pending-banner">
          <div className="flex items-center gap-2">
            <AlertTriangle size={16} />
            <span><strong>{totalPending}</strong> receipt{totalPending > 1 ? 's' : ''} pending approval ({fmt(totalPendingAmt)}). Expand the call to Approve or Reject — or approve them all in one click.</span>
          </div>
          {(isAdmin || isManager) && (
            <button
              onClick={() => handleBulkApprove(null)}
              disabled={actionLoading === 'bulk'}
              className="inline-flex items-center gap-1 px-3 py-1.5 text-xs font-medium rounded-md bg-amber-600 text-white hover:bg-amber-700 disabled:opacity-50"
              data-testid="bulk-approve-all-btn">
              <CheckCheck size={14} />
              {actionLoading === 'bulk' ? 'Approving…' : `Approve All Pending (${totalPending})`}
            </button>
          )}
        </div>
      )}

      <div className="bg-white border rounded-lg overflow-hidden">
        <table className="w-full text-sm">
          <thead>
            <tr className="bg-gray-50 border-b text-left text-gray-600 text-xs uppercase tracking-wider">
              <th className="px-3 py-2 w-8"></th>
              <th className="px-3 py-2">Drawdown</th>
              <th className="px-3 py-2">Fund</th>
              <th className="px-3 py-2">Class</th>
              <th className="px-3 py-2 text-center">Status</th>
              <th className="px-3 py-2">Due Date</th>
              <th className="px-3 py-2 text-right">Called</th>
              <th className="px-3 py-2 text-right">Received</th>
              <th className="px-3 py-2 text-right">Outstanding</th>
              <th className="px-3 py-2 text-center">% Collected</th>
              <th className="px-3 py-2 w-12"></th>
            </tr>
          </thead>
          <tbody>
            {rows.map(r => {
              const isOpen = !!expanded[r.drawdown_investor_id];
              const pendingCount = (r.receipts || []).filter(x => x.approval_status === 'pending_approval').length;
              return (
                <React.Fragment key={r.drawdown_investor_id}>
                  <tr className="border-b hover:bg-gray-50 cursor-pointer"
                      onClick={() => setExpanded(p => ({ ...p, [r.drawdown_investor_id]: !isOpen }))}
                      data-testid={`dc-row-${r.drawdown_id}`}>
                    <td className="px-3 py-3">
                      {isOpen ? <ChevronDown size={14} className="text-gray-400" /> : <ChevronRight size={14} className="text-gray-400" />}
                    </td>
                    <td className="px-3 py-3">
                      <div className="font-medium text-gray-900">{r.drawdown_name || r.drawdown_number}</div>
                      <div className="text-xs text-gray-500 font-mono">{r.drawdown_number}</div>
                    </td>
                    <td className="px-3 py-3 text-gray-700">{r.fund_name}</td>
                    <td className="px-3 py-3">
                      {r.class_name ? <span className="px-2 py-0.5 bg-gray-100 rounded text-xs">{r.class_name}</span> : <span className="text-gray-400">—</span>}
                    </td>
                    <td className="px-3 py-3 text-center">
                      <span className={`px-2 py-0.5 rounded-full text-xs font-medium capitalize ${statusColors[r.status] || 'bg-gray-100'}`}>{label(r.status)}</span>
                      {pendingCount > 0 && (
                        <span className="ml-1 px-1.5 py-0.5 text-[10px] rounded-full bg-amber-100 text-amber-700 font-medium" data-testid={`dc-pending-badge-${r.drawdown_id}`}>
                          {pendingCount} pending
                        </span>
                      )}
                      {pendingCount >= 2 && (isAdmin || isManager) && (
                        <button
                          onClick={(e) => { e.stopPropagation(); handleBulkApprove(r.drawdown_id); }}
                          disabled={actionLoading === 'bulk'}
                          className="ml-1 inline-flex items-center gap-0.5 px-1.5 py-0.5 text-[10px] rounded bg-amber-600 text-white hover:bg-amber-700 disabled:opacity-50"
                          title={`Approve all ${pendingCount} pending tranches on this drawdown`}
                          data-testid={`dc-bulk-approve-row-${r.drawdown_id}`}>
                          <CheckCheck size={10} /> Approve all
                        </button>
                      )}
                    </td>
                    <td className="px-3 py-3 text-gray-600">{r.due_date || '—'}</td>
                    <td className="px-3 py-3 text-right font-medium text-indigo-700">{fmt(r.called_amount)}</td>
                    <td className="px-3 py-3 text-right text-green-700">{fmt(r.received_amount)}</td>
                    <td className={`px-3 py-3 text-right font-semibold ${r.outstanding_amount > 0 ? 'text-red-600' : 'text-gray-400'}`}>{fmt(r.outstanding_amount)}</td>
                    <td className="px-3 py-3 text-center">
                      <div className="inline-flex items-center gap-1">
                        <span className="text-xs text-gray-700">{r.pct_collected}%</span>
                      </div>
                    </td>
                    <td className="px-3 py-3 text-right">
                      <Link to={`/drawdowns/${r.drawdown_id}`}
                            onClick={e => e.stopPropagation()}
                            className="text-gray-400 hover:text-indigo-600 inline-block" title="Open drawdown">
                        <ExternalLink size={14} />
                      </Link>
                    </td>
                  </tr>

                  {/* Expanded receipts */}
                  {isOpen && (r.receipts || []).length === 0 && (
                    <tr key={`${r.drawdown_investor_id}-empty`} className="bg-gray-50/60 border-b">
                      <td colSpan={11} className="px-12 py-3 text-xs text-gray-500 italic">
                        No receipts recorded yet for this call.
                      </td>
                    </tr>
                  )}
                  {isOpen && (r.receipts || []).map((rcpt, idx) => {
                    const rStatus = rcpt.approval_status || 'approved';
                    const canApprove = rStatus === 'pending_approval' && (isAdmin || isManager);
                    return (
                      <tr key={rcpt.id} className="bg-gray-50/80 border-b text-xs" data-testid={`dc-receipt-${rcpt.id}`}>
                        <td></td>
                        <td className="px-3 py-2 pl-8 text-gray-500" colSpan={2}>Tranche {idx + 1}</td>
                        <td className="px-3 py-2"></td>
                        <td className="px-3 py-2 text-center">
                          <span className={`px-2 py-0.5 rounded-full text-[10px] font-medium capitalize ${trancheColors[rStatus] || 'bg-gray-100'}`}>{label(rStatus)}</span>
                        </td>
                        <td className="px-3 py-2 text-gray-600">{rcpt.date_of_receipt}</td>
                        <td className="px-3 py-2"></td>
                        <td className="px-3 py-2 text-right font-medium text-green-700">{fmt(rcpt.amount)}</td>
                        <td className="px-3 py-2 text-gray-500 italic">
                          {rStatus === 'rejected' && rcpt.rejection_reason
                            ? <span className="text-red-600">Rejected: {rcpt.rejection_reason}</span>
                            : (rcpt.soa_remarks || '')}
                        </td>
                        <td className="px-3 py-2"></td>
                        <td className="px-3 py-2">
                          {canApprove && (
                            <div className="flex items-center gap-1 justify-end">
                              <button
                                onClick={(e) => { e.stopPropagation(); handleApprove(r, rcpt.id); }}
                                disabled={actionLoading === rcpt.id}
                                className="p-1 text-green-600 hover:bg-green-100 rounded disabled:opacity-50"
                                title="Approve receipt"
                                data-testid={`dc-approve-${rcpt.id}`}><Check size={14} /></button>
                              <button
                                onClick={(e) => { e.stopPropagation(); setRejectModal({ row: r, receiptId: rcpt.id, reason: '' }); }}
                                disabled={actionLoading === rcpt.id}
                                className="p-1 text-red-600 hover:bg-red-100 rounded disabled:opacity-50"
                                title="Reject receipt"
                                data-testid={`dc-reject-${rcpt.id}`}><X size={14} /></button>
                            </div>
                          )}
                        </td>
                      </tr>
                    );
                  })}
                </React.Fragment>
              );
            })}
          </tbody>
        </table>
      </div>

      {rejectModal && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4" data-testid="dc-reject-modal">
          <div className="bg-white rounded-xl shadow-xl w-full max-w-md p-6">
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-lg font-semibold text-red-700">Reject Receipt</h3>
              <button onClick={() => setRejectModal(null)} className="text-gray-400 hover:text-gray-600"><X size={20} /></button>
            </div>
            <p className="text-sm text-gray-600 mb-3">Provide a reason so the maker can fix and re-submit.</p>
            <textarea value={rejectModal.reason} onChange={e => setRejectModal(p => ({ ...p, reason: e.target.value }))}
              rows={3} placeholder="Reason for rejection"
              className="w-full border rounded-lg px-3 py-2 text-sm" data-testid="dc-reject-reason" />
            <div className="flex gap-2 justify-end mt-4">
              <button onClick={() => setRejectModal(null)} className="px-3 py-2 text-sm border rounded-lg">Cancel</button>
              <button onClick={handleReject} disabled={actionLoading}
                className="px-4 py-2 text-sm bg-red-600 text-white rounded-lg disabled:opacity-50"
                data-testid="dc-confirm-reject">Reject</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default DrawdownCallsSection;
